/**
 * ScoreCell - Celda de puntuación para tablas
 * 
 * Muestra score original → score nuevo con colores por rango.
 * Soporta estado vacío (sin coordenadas).
 * 
 * @source P1_Normaliza_v7_fixed.html
 * @version 1.1.0 — migrado a DS v2 (F8.06b)
 */

import React from 'react';
import { 
  colors, 
  typography, 
  borderRadius,
  getScoreColor,
  getScoreClass,
} from '@/design-system/v2';

export interface ScoreCellProps {
  /** Puntuación original (antes de normalizar) */
  originalScore?: number | null;
  /** Puntuación nueva (después de normalizar) */
  newScore?: number | null;
  /** Mostrar solo el score nuevo (sin original ni flecha) */
  showOnlyNew?: boolean;
}

const STYLES = {
  container: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
  } as React.CSSProperties,
  
  original: {
    color: colors.fg.muted,
    display: 'inline-block',
    minWidth: '20px',
    textAlign: 'right' as const,
  } as React.CSSProperties,
  
  arrow: {
    color: colors.fg.subtle,
    fontSize: '10px',
  } as React.CSSProperties,
  
  newScore: {
    fontWeight: typography.fontWeight.semibold,
    minWidth: '36px',
    padding: '4px 8px',
    borderRadius: borderRadius.sm,
    textAlign: 'center' as const,
  } as React.CSSProperties,
  
  empty: {
    display: 'block',
    textAlign: 'center' as const,
    color: colors.fg.subtle,
  } as React.CSSProperties,
};

export const ScoreCell: React.FC<ScoreCellProps> = ({ 
  originalScore, 
  newScore, 
  showOnlyNew = false 
}) => {
  // Sin datos — score null O score 0 (sin coordenadas, pipeline no evaluado)
  if (newScore == null || newScore === 0) {
    return <span style={STYLES.empty}>—</span>;
  }

  const scoreClass = getScoreClass(newScore);
  const newStyle: React.CSSProperties = {
    ...STYLES.newScore,
    backgroundColor: colors.scoresBg[scoreClass],
    color: getScoreColor(newScore),
  };

  // Solo score nuevo
  if (showOnlyNew || originalScore == null) {
    return (
      <div style={STYLES.container}>
        <span style={newStyle}>{newScore}</span>
      </div>
    );
  }

  // Formato completo: original → nuevo
  return (
    <div style={STYLES.container}>
      <span style={STYLES.original}>{originalScore}</span>
      <span style={STYLES.arrow}>→</span>
      <span style={newStyle}>{newScore}</span>
    </div>
  );
};

export default ScoreCell;
